import {
  GazePositionKey,
  GazePositionResult,
  MotilityAnalysis,
  MotilityEye,
  MuscleCode,
} from "types/testTypes";

export const MOTILITY_TR = "extraTests.ocularMotility.muscles";

/** Reading order of the 3x3 grid, left to right and top to bottom. */
export const GAZE_GRID_ORDER: GazePositionKey[] = [
  "upLeft",
  "up",
  "upRight",
  "left",
  "primary",
  "right",
  "downLeft",
  "down",
  "downRight",
];

export const MOTILITY_EYES: MotilityEye[] = ["right", "left"];

/** Mirrors the backend's UNDERACTION_ and OVERACTION_RATIO_THRESHOLD. */
const UNDERACTION_RATIO_THRESHOLD = 0.875;
const OVERACTION_RATIO_THRESHOLD = 1.125;

/** Shade tracks distance from a complete movement, so a half-reach stands out. */
export const excursionColor = (ratio: number | null): string => {
  if (ratio === null) return "var(--mantine-color-gray-4)";
  if (ratio >= UNDERACTION_RATIO_THRESHOLD && ratio <= OVERACTION_RATIO_THRESHOLD) {
    return "var(--mantine-color-gray-6)";
  }
  const shade = Math.min(9, 3 + Math.abs(ratio - 1) * 6);
  const hue = ratio < 1 ? "red" : "blue";
  return `var(--mantine-color-${hue}-${Math.round(shade)})`;
};

/**
 * A percentage, not a -4..+4 grade: that scale is ordinal. Bounded because the
 * ratio is not: a target barely past the primary cut-off gives a tiny expected
 * excursion and an arbitrarily large quotient, and the reading has to fit a
 * fixed column. Anything past the bound is a tracking failure, not a finding.
 */
const EXCURSION_DISPLAY_BOUND = 9.99;

export const formatExcursion = (ratio: number | null, fallback = "—"): string => {
  if (ratio === null) return fallback;
  if (ratio > EXCURSION_DISPLAY_BOUND) return ">999%";
  if (ratio < -EXCURSION_DISPLAY_BOUND) return "<-999%";
  return `${Math.round(ratio * 100)}%`;
};

/**
 * Where the target outran the duction limit only falling short of that limit
 * means anything; reaching past it says the stimulus was wider than normal
 * duction, as the old XIH pattern was (71 degrees obliquely against a 39-degree
 * limit). Reported as a floor rather than as overaction.
 */
export const readExcursion = (
  ratio: number | null,
  referenceCappedToDuctionLimit: boolean,
): { label: string; color: string; beyondLimit: boolean } => {
  const beyondLimit = referenceCappedToDuctionLimit && ratio !== null && ratio > 1;
  return {
    label: beyondLimit ? "≥100%" : formatExcursion(ratio),
    color: excursionColor(beyondLimit ? 1 : ratio),
    beyondLimit,
  };
};

export const formatPrismDioptres = (value: number | null | undefined, fallback = "—"): string =>
  typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(1)}Δ` : fallback;

export const formatDegrees = (value: number | null | undefined, fallback = "—"): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  // toFixed keeps the sign of a negative that rounds to zero, printing "-0.0".
  const rounded = Number(value.toFixed(1));
  return `${rounded === 0 ? 0 : rounded.toFixed(1)}°`;
};

export const muscleLabelKey = (muscle: MuscleCode): string =>
  `${MOTILITY_TR}.muscleNames.${muscle}`;

export const positionLabelKey = (position: GazePositionKey): string =>
  `${MOTILITY_TR}.positions.${position}`;

/** Up and down have no single prime mover, so the cell names the group instead. */
export const positionMuscleFallbackKey = (position: GazePositionKey): string | null => {
  if (position === "up") return `${MOTILITY_TR}.grid.elevation`;
  if (position === "down") return `${MOTILITY_TR}.grid.depression`;
  return null;
};

export const findPosition = (
  analysis: MotilityAnalysis | null | undefined,
  position: GazePositionKey,
): GazePositionResult | null =>
  analysis?.gazeGrid?.find((entry) => entry.position === position) ?? null;

/** True when there is enough in the analysis to be worth rendering a report for. */
const hasMotilityAnalysis = (
  analysis: MotilityAnalysis | null | undefined,
): analysis is MotilityAnalysis => Boolean(analysis?.gazeGrid?.length);

export const readMotilityAnalysis = (
  results: { motilityAnalysis?: MotilityAnalysis } | null | undefined,
): MotilityAnalysis | null => {
  const analysis = results?.motilityAnalysis;
  return hasMotilityAnalysis(analysis) ? analysis : null;
};
